
import Vue from 'vue';
import { Data as ArticleData } from '@root/modules/article/types/article';
import ArticleTags from '@root/modules/article/components/ArticleTags.vue';
import ArticleSource from '@root/modules/article/components/ArticleSource.vue';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import getBannerKey from '@root/modules/ads/utils/getBannerKey';
import rootConfig from '@root/config/root.config';
import { loadScripts } from '@root/common/utils/scriptLoader';
import hasNapoComments from '@root/modules/article/utils/hasNapoComments';

interface Props {
  article: ArticleData;
}

interface Data {
  isClient: boolean;
  showComments: boolean;
  sourcesActive: boolean;
  tagsActive: boolean;
}

interface Computed {
  articleUrl: string;
  articleCommentsPath: string;
  disableAds: boolean;
  isCrArticle: boolean;
  napoComments: boolean;
  napoContainer: string;
  showBottomText: boolean;
}

interface Methods {
  getKey: (params: Record<string, any>) => string;
  loadNapo: () => void;
  loadNapoEmbed: () => void;
  toggleCollapse: (section: string) => void;
}

interface CommentsWindow extends Window {
  napo: any;
}

declare let window: CommentsWindow;

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleTags,
    ArticleSource,
    AdFormBanner,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isClient: process.client,
      showComments: false,
      sourcesActive: true,
      tagsActive: false,
    };
  },
  computed: {
    articleUrl() {
      const { domain, locale } = this.$channelConfig('settings');
      return this.$options.filters!.articleUrl({ article: this.article, locale, domain, fakeDomainEnabled: this.$config.FAKE_DOMAIN_ENABLED });
    },
    articleCommentsPath() {
      const { domain, locale } = this.$channelConfig('settings');
      return this.$options.filters!.articleCommentsUrl({ article: this.article, locale, domain, fakeDomainEnabled: this.$config.FAKE_DOMAIN_ENABLED });
    },
    disableAds() {
      return this.article.banners.disableAds;
    },
    isCrArticle() {
      const articles = this.$store.getters['article/getCrArticles'];

      return articles.find((article: number) => article === this.article.id);
    },
    napoComments() {
      return hasNapoComments(this.article);
    },
    napoContainer() {
      return `comments-fe-teaser-${this.article.id}`;
    },
    showBottomText() {
      const { showBottomText } = this.$channelConfig('page').article.component;

      return showBottomText || false;
    },
  },
  mounted() {
    if (process.client && this.napoComments) {
      this.$nextTick(() => {
        this.loadNapo();
      });
    }
  },
  methods: {
    getKey({ prefix, index }) {
      return getBannerKey({ prefix, route: this.$route, store: this.$store, index });
    },
    loadNapo() {
      // Do not load napo script if it is already loaded
      if (!window.napo) {
        loadScripts(rootConfig.napo_url).then(() => {
          this.loadNapoEmbed();
        });
      } else {
        this.loadNapoEmbed();
      }
    },
    loadNapoEmbed() {
      window.napo.push(['_embedTeaser', this.napoContainer, this.article.id, `${location.protocol}//${location.host}${this.articleUrl}/napo`]);
    },
    toggleCollapse(section) {
      // Switch active state
      if (section === 'source') {
        this.sourcesActive = !this.sourcesActive;

        if (this.sourcesActive) {
          this.tagsActive = false;
        }
      } else {
        this.tagsActive = !this.tagsActive;

        if (this.tagsActive) {
          this.sourcesActive = false;
        }
      }

      const id = this.article.id;
      const targetNavigationItem = window.document.querySelector(`.article-body-bottom__collapse-navigation--item-${section}-${id}`) as HTMLElement;
      const targetContainer = window.document.querySelector(`.article-body-bottom__collapse-container--${section}-${id}`) as HTMLElement;
      const targetContainerIsActive = targetContainer.classList.contains('article-body-bottom__collapse-container--active');

      const navigationItems = window.document.querySelectorAll('.article-body-bottom__collapse-navigation--item');
      navigationItems.forEach((element) => {
        const navigationItem = element as HTMLElement;
        navigationItem.classList.remove('article-body-bottom__collapse-navigation--item--active');
      });

      const containers = window.document.querySelectorAll('.article-body-bottom__collapse-container');

      containers.forEach((element) => {
        const container = element as HTMLElement;
        container.classList.remove('article-body-bottom__collapse-container--active');
      });

      if (!targetContainerIsActive) {
        targetNavigationItem.classList.toggle('article-body-bottom__collapse-navigation--item--active');
        targetContainer.classList.toggle('article-body-bottom__collapse-container--active');
      }
    },
  },
});
